// @flow

import * as React from 'react';
import { createRefetchContainer, graphql } from 'react-relay/compat';
import CableStore from 'app/stores/CableStore';
import JobEventsChannel from 'app/channels/JobEventsChannel';
import JobEventActorFigure from './JobEventActorFigure';
import JobEventCreated from './JobEventCreated';
import JobEventLimited from './JobEventLimited';
import JobEventFinished from './JobEventFinished';
import JobEventRetried from './JobEventRetried';
import JobEventScheduled from './JobEventScheduled';
import JobEventAssigned from './JobEventAssigned';
import JobEventCanceled from './JobEventCanceled';
import JobEventCancelation from './JobEventCancelation';
import JobEventAccepted from './JobEventAccepted';
import JobEventStarted from './JobEventStarted';
import JobEventTimedOut from './JobEventTimedOut';
import JobEventChanged from './JobEventChanged';
import JobEventBuildStepUploadCreated from './JobEventBuildStepUploadCreated';

import type { JobActivityTimelineList_job, JobEventType, JobStates } from './__generated__/JobActivityTimelineList_job.graphql';

// This is pretty gnarly but will just extract the nested event node type from the generated types,
// which is pretty handy as we pass it around a bunch here...
type EventNode = $Call<
  <T>({ +events?: { +edges: ?$ReadOnlyArray<?{| +node: T |}> } }) => T,
  JobActivityTimelineList_job
>;
// “Fake” events that are UI only
type VirtualEventNode = { type: "CREATED" | "PENDING" };

type Props = {
  relay: *,
  job: JobActivityTimelineList_job
};

// Create a constant that contains all the possible events types, type it by the possible
// values from the enum.
export const EVENT_TYPES: {[JobEventType]: JobEventType} = {
  LIMITED: "LIMITED",
  ACCEPTED: "ACCEPTED",
  ASSIGNED: "ASSIGNED",
  CANCELATION: "CANCELATION",
  CANCELED: "CANCELED",
  CHANGED: "CHANGED",
  FINISHED: "FINISHED",
  NOTIFICATION: "NOTIFICATION",
  RETRIED: "RETRIED",
  SCHEDULED: "SCHEDULED",
  STARTED: "STARTED",
  TIMED_OUT: "TIMED_OUT",
  UNBLOCKED: "UNBLOCKED",
  BUILD_STEP_UPLOAD_CREATED: "BUILD_STEP_UPLOAD_CREATED"
};

export const UI_EVENT_TYPES = {
  // Special case types that only exist in the UI
  CREATED: "CREATED",
  PENDING: "PENDING"
};

const RENDERABLE_EVENT_TYPES = [
  EVENT_TYPES.LIMITED,
  EVENT_TYPES.SCHEDULED,
  EVENT_TYPES.ASSIGNED,
  EVENT_TYPES.ACCEPTED,
  EVENT_TYPES.STARTED,
  EVENT_TYPES.FINISHED,
  EVENT_TYPES.RETRIED,
  EVENT_TYPES.CANCELED,
  EVENT_TYPES.CANCELATION,
  EVENT_TYPES.TIMED_OUT,
  EVENT_TYPES.BUILD_STEP_UPLOAD_CREATED
];

function EventContent(props: { event: EventNode }): React.Node {
  // See Job::Event::Type in app/models/job/event.rb
  switch (props.event?.type) {
    case EVENT_TYPES.LIMITED: return <JobEventLimited {...props} />;
    case EVENT_TYPES.SCHEDULED: return <JobEventScheduled {...props} />;
    case EVENT_TYPES.ASSIGNED: return <JobEventAssigned {...props} />;
    case EVENT_TYPES.ACCEPTED: return <JobEventAccepted {...props} />;
    case EVENT_TYPES.STARTED: return <JobEventStarted {...props} />;
    case EVENT_TYPES.FINISHED: return <JobEventFinished {...props} />;
    case EVENT_TYPES.RETRIED: return <JobEventRetried {...props} />;
    case EVENT_TYPES.CANCELATION: return <JobEventCancelation {...props} />;
    case EVENT_TYPES.CANCELED: return <JobEventCanceled {...props} />;
    case EVENT_TYPES.TIMED_OUT: return <JobEventTimedOut {...props} />;
    case EVENT_TYPES.CHANGED: return <JobEventChanged {...props} />;
    case EVENT_TYPES.BUILD_STEP_UPLOAD_CREATED: return <JobEventBuildStepUploadCreated {...props} />;

    // Not implemented yet...
    case EVENT_TYPES.NOTIFICATION:
    case EVENT_TYPES.UNBLOCKED: return null;

    // Fall through to an empty render. In case something weird happens, lol?
    default: return null;
  }
}

function EventBadge(props: { event: EventNode | VirtualEventNode }): React.Node {
  switch (props.event?.type) {
    case UI_EVENT_TYPES.CREATED: return <JobEventActorFigure type={UI_EVENT_TYPES.CREATED} />;
    case UI_EVENT_TYPES.PENDING: return <JobEventActorFigure type={UI_EVENT_TYPES.PENDING} />;
    default: return <JobEventActorFigure type="NODE" />;
  }
}

function hasReachedTerminalState(state: ?JobStates): boolean {
  switch (state) {
    case "ACCEPTED":
    case "ASSIGNED":
    case "CANCELING":
    case "LIMITED":
    case "LIMITING":
    case "RUNNING":
    case "SCHEDULED":
    case "TIMING_OUT":
    case "WAITING":
      return false;
    default:
      return true;
  }
}

function JobActivityTimelineList({ job, relay }: Props) {
  React.useEffect(() => {
    // This is doing some weird unique reaching into the channel system when it
    // probably shouldn't. The CableStore doesn't return a subscription you can
    // later unsubscribe from, though, and also doesn't provide the low-level
    // `received` hook this uses. It should probably be listening to specific
    // events, though... I'm not sure why it was done like this.
    //
    // In any case, we make sure the CableStore is configured just in case it
    // would break something given the weirdness this does.
    //
    // - Jess, September 2020
    if (!CableStore.isConfigured) {
      CableStore.configure();
    }

    const subscription = JobEventsChannel.subscribe({ uuid: job.uuid }, {
      received: (data: Object) => {
        if (data.changed) {
          relay.refetch();
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [job.uuid]);

  return (
    <div className="JobActivityTimeline pt2 pb2" data-testid="JobActivityTimelineComponent">
      <ol className="JobActivityTimeline__list" data-testid="JobActivityTimelineComponentList">
        {/*
        Render a virtual event that represents the creation event for the job. We don’t
        actually have an event in the DB for this, we just fake it using the createdAt of
        the Job itself. Fake it til’ ya make it!
        */}
        <li
          key="CREATED"
          className="JobActivityTimeline__list__item"
          data-testid="JobActivityTimelineComponentListItem"
        >
          <div className="flex">
            <EventBadge event={{ type: UI_EVENT_TYPES.CREATED }} />
            <div className="JobActivityTimeline__list__item__content flex-auto">
              <JobEventCreated job={job} />
            </div>
          </div>
        </li>

        {/* If we have some jobs... */}
        {job && job.events && job.events.edges ? (
          // Render out our job records.!
          job.events.edges.map((edge, index) => {
            // Guard against weird nulls here.
            if (!edge || !edge.node) {
              return null;
            }

            const { node } = edge;

            // Is this event on that we support rendering on the client? If not then we’ll
            // just skip over it for now.
            if (!RENDERABLE_EVENT_TYPES.includes(node.type)) {
              return null;
            }

            // Fetch the previous event timestamp (if it exists) so that we can render
            // nice incremental time diffs between each edge.
            const previousEdge = job.events && job.events.edges && job.events.edges[index - 1];
            const previousTimestamp = previousEdge && previousEdge.node ? previousEdge.node.timestamp : job.createdAt;

            return (
              <li
                key={node.uuid}
                className="JobActivityTimeline__list__item"
                data-testid="JobActivityTimelineComponentListItem"
              >
                <div className="flex">
                  <EventBadge event={node} />
                  <div className="JobActivityTimeline__list__item__content flex-auto">
                    <EventContent event={node} previousTimestamp={previousTimestamp} />
                  </div>
                </div>
              </li>
            );
          })
        ) : null}

        {/*
        Unless the job has reached a terminal state we want to render an indeterminate progress
        spinner/indicator here to communicate that buildkite expects that things are still happening!
        */}
        {job && !hasReachedTerminalState(job.state) ? (
          <li key="WAITING" className="JobActivityTimeline__list__item">
            <div className="flex">
              <EventBadge event={{ type: UI_EVENT_TYPES.PENDING }} />
              <div className="JobActivityTimeline__list__item__content flex-auto dark-gray" />
            </div>
          </li>
        ) : null}
      </ol>
    </div>
  );
}


export default createRefetchContainer(
  JobActivityTimelineList,
  graphql`
    fragment JobActivityTimelineList_job on Job {
      ... on JobTypeCommand {
        uuid
        state
        createdAt
        ...JobEventCreated_job

        events(first: 30) {
          edges {
            node {
              uuid
              ...on JobEvent{
                timestamp
                type

                ...JobEventLimited_event
                ...JobEventFinished_event
                ...JobEventRetried_event
                ...JobEventScheduled_event
                ...JobEventAssigned_event
                ...JobEventCanceled_event
                ...JobEventCancelation_event
                ...JobEventAccepted_event
                ...JobEventStarted_event
                ...JobEventTimedOut_event
                ...JobEventChanged_event
                ...JobEventBuildStepUploadCreated_event
              }
            }
          }
        }
      }
    }
  `,
  graphql`
    query JobActivityTimelineListRefetchQuery($id: ID!) {
      job(uuid: $id) {
        ...JobActivityTimelineList_job
      }
    }
  `
);
